// @flow
import React, { useState, useEffect, useReducer } from 'react';
import useSWR from 'swr';
import api from '~/_base/constants/api';
import buildTzoneList from '~/_base/functions/buildTzoneList';
import {
  Dialog,
  Typography,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Button,
  Switch,
  Tooltip,
  InputBase,
  Box,
} from '@mui/material';
import { ILoader, IShield } from '~/_base/components/helixIcons';
import ApiCall from '~/_base/functions/apicalls';
import baseAddress from '~/_base/functions/baseAddress';
import ChangeLog from '~/_base/components/changeLog';
import Diagnostics from '~/_base/components/diagnostics';
import Policy from '~/_base/classes/policy';
import MaterialFormGrid, {
  MaterialFormGridControl,
  MaterialFormGridCollapse,
} from '~/_base/components/materialFormGrid';
import InlineHelp from '~/_base/components/inlineHelp';
import { useToast } from '~/_base/context/toastContext';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';

type Props = {
  onSave?: () => void,
  onClose: () => void,
  open: boolean,
  id: number,
  type: string,
};

const tznames = buildTzoneList();
function userReducer(state, action) {
  switch (action.type) {
    case 'CLEAR_USER_STATE':
      return {
        userObject: undefined,
        userPolicy: undefined,
        userChangeLog: undefined,
      };
    case 'UPDATE_USER_OBJECT':
      return {
        ...state,
        userObject: { ...state.userObject, ...action.payload },
      };
    case 'UPDATE_USER':
      return {
        ...state,
        ...action.payload,
      };
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

export default function UserDialog(props: Props) {
  const { data: currentContext } = useSWR(
    api.currentContext.address,
    api.currentContext.request
  );

  const { errorToast, warningToast, successToast } = useToast();
  const [gettingData, setGettingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [userState, userDispatch] = useReducer(userReducer, {
    userObject: undefined,
    userPolicy: undefined,
    userChangeLog: undefined,
  });
  const [shouldValidate, setShouldValidate] = useState(false);
  const { t } = useTranslation();
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [jobTitleOptions, setJobTitleOptions] = useState([]);

  useEffect(() => {
    async function getUser(id: number) {
      let isNew = id == null;
      try {
        const res = await api.userData.request(isNew ? 'new' : id, props.type);
        if (res.notice) {
          successToast(res.notice);
        }
        if (res.alert) {
          warningToast(res.alert);
        }
        setGettingData(false);

        // Initialize "managed" value in case of returning null
        if (res.entity?.managed === null) {
          res.entity.managed = false;
        }

        userDispatch({
          type: 'UPDATE_USER',
          payload: {
            userObject: res.entity,
            userPolicy: res.policy,
            userChangeLog: res.change_log,
          },
        });
      } catch (err) {
        errorToast(err.message);
        setGettingData(false);
      }
    }

    props.open === true && getUser(props.id);
  }, [
    errorToast,
    props.id,
    props.open,
    props.type,
    successToast,
    warningToast,
  ]);

  useEffect(() => {
    // Fetch unique department/job title options once for this dialog
    (async () => {
      try {
        const response = await ApiCall(`${baseAddress()}/users.json`, 'GET');
        const users = (response && response.items) || [];
        if (users.length === 0) return;
        const departmentSet = new Set();
        const jobTitleSet = new Set();
        for (const u of users) {
          if (u?.department) departmentSet.add(u.department);
          if (u?.job_title) jobTitleSet.add(u.job_title);
        }
        setDepartmentOptions(Array.from(departmentSet).map(label => ({ label })));
        setJobTitleOptions(Array.from(jobTitleSet).map(label => ({ label })));
      } catch (e) {
        throw new Error('could not get data for department and job title options');
      }
    })();
  }, []);

  async function saveChange() {
    setShouldValidate(true);

    let hasErrors = !Policy.isValid(userState.userObject, userState.userPolicy);
    if (hasErrors) {
      warningToast(t('Please fix errors before saving'));
    } else {
      setSaving(true);
      let isUpdate = props.id != null;
      let body = JSON.stringify(userState.userObject);
      try {
        let res;
        if (isUpdate) {
          res = await api.updateUser.request(props.id, body);
        } else {
          res = await api.createUser.request(body);
        }
        if (res.notice) {
          successToast(res.notice);
        }
        if (res.alert) {
          warningToast(res.alert);
        }
        setSaving(false);
        setShouldValidate(false);
        props.onSave && props.onSave();
      } catch (err) {
        errorToast(err.message);
        setSaving(false);
        setShouldValidate(false);
      }
    }
  }

  function updateUserObject(payload: any) {
    if (!userState.userObject) return;
    setDirty(true);
    userDispatch({
      type: 'UPDATE_USER_OBJECT',
      payload,
    });
  }

  const isSupportUser = currentContext.current_user.id === 1;
  const isUserManaged = (userState.userObject || {}).managed;
  const isControlsDisabled = !isSupportUser && isUserManaged;

  let isNew = props.id == null;
  let userType = (userState.userObject || {}).user_type;
  let superPowersOn = userType === 'standard';
  let isExternal = userType === 'external';
  let showChangeLog =
    !isNew && Policy.setting(userState.userPolicy, 'change_log?');
  let showDiagnostics =
    !isNew && Policy.setting(userState.userPolicy, 'change_log_with_links?');

  let bindingProps = {
    source: userState.userObject,
    policy: userState.userPolicy,
  };

  const titleHelpKey = isNew ? 'new_user' : 'edit_user';


  const jobTitle = { label: bindingProps?.source?.job_title ?? '' };
  const department = { label: bindingProps?.source?.department ?? '' };

  return (
    <Dialog
      data-testid="user_dialog"
      open={props.open}
      onClose={e => {
        userDispatch({ type: 'CLEAR_USER_STATE' });
        setSelectedTab(0);
        props.onClose();
        setShouldValidate(false);
      }}
      slotProps={{
        paper: {
          sx: {
            width: '90vw',
            maxWidth: '600px',
            height: '90vh',
            maxHeight: '800px',
          },
        }
      }}
    >
      <DialogTitle sx={{ pl: 0, pr: 0, pb: 0 }}>
        <Typography variant="h3" sx={{ pl: 3, pb: 3, pr: 3 }}>
          {t('{{createOrEdit}} {{userType}} User', {
            createOrEdit: isNew ? t('Create') : t('Edit'),
            userType: isExternal ? t('External') : '',
          })}
          {titleHelpKey && <InlineHelp helpkey={titleHelpKey} />}

          {isControlsDisabled ? (
            <div>
              <Typography
                variant="subtitle2"
                sx={{ fontSize: '12px', color: '#d93240' }}
              >
                {t(
                  'Editing not available: this user is being managed externally'
                )}
              </Typography>
            </div>
          ) : null}
          <Box sx={{ position: 'absolute', right: 2, top: 2 }}>
            {!isNew && (isSupportUser || isUserManaged) ? (
              <MaterialFormGridControl
                {...bindingProps}
                prop="managed"
                label={<span>{t('Managed')}</span>}
                switchComponent={<Switch color="primary" />}
                disabled={isControlsDisabled || !isSupportUser}
                tooltip={t('Managed by TWMS')}
                onChange={(prop, val) =>
                  val != null ? updateUserObject({ [prop]: val }) : undefined
                }
              />
            ) : null}

            <MaterialFormGridControl
              {...bindingProps}
              prop="active"
              label={<span>{t('Active')}</span>}
              switchComponent={<Switch color="primary" />}
              onChange={(prop, val) =>
                val != null ? updateUserObject({ [prop]: val }) : undefined
              }
            />
          </Box>
        </Typography>
        <Tabs
          value={selectedTab}
          onChange={(e, value) => setSelectedTab(value)}
        >
          <Tab
            sx={{ minWidth: 0 }}
            key="Details"
            label={t('Details')}
          />
          {showChangeLog && userState.userChangeLog && (
            <Tab
              sx={{ minWidth: 0 }}
              key="Change Log"
              label={t('Change Log')}
            />
          )}
          {showDiagnostics && userState.userPolicy && (
            <Tab
              sx={{ minWidth: 0 }}
              key="Diagnostics"
              label={t('Diagnostics')}
            />
          )}
        </Tabs>
      </DialogTitle>
      <DialogContent sx={{ borderTop: '1px solid #e6eaec', marginTop: '-1px' }}>
        {gettingData ? (
          <ILoader fontSize="large" />
        ) : (
          <React.Fragment>
            {selectedTab === 0 && (
              <MaterialFormGrid
                {...bindingProps}
                onChange={(prop, val) => updateUserObject({ [prop]: val })}
              >
                <MaterialFormGridControl
                  size={12}
                  label={t('Full name')}
                  prop="name"
                  disabled={isControlsDisabled}
                  disabledTooltip={t(
                    'Editing not available: this user is being managed externally'
                  )}
                  shouldValidate={shouldValidate}
                />
                <MaterialFormGridControl
                  size={12}
                  label={t('Email')}
                  prop="email"
                  disabled={isControlsDisabled}
                  disabledTooltip={t(
                    'Editing not available: this user is being managed externally'
                  )}
                  shouldValidate={shouldValidate}
                />
                <MaterialFormGridControl
                  size={12}
                  label={t('Time zone')}
                  prop="time_zone"
                  options={tznames}
                  disabled={isControlsDisabled}
                  disabledTooltip={t(
                    'Editing not available: this user is being managed externally'
                  )}
                />
                {!isNew && (isSupportUser || isUserManaged) ? (
                  <MaterialFormGridControl
                    size={12}
                    label={t('Damstra ID')}
                    prop="damstra_id"
                    disabled={isControlsDisabled}
                    disabledTooltip={t(
                      'Editing not available: this user is being managed externally'
                    )}
                  />
                ) : null}
                <>
                  <MaterialFormGridControl
                    size={12}
                  >
                    <label htmlFor={currentContext.current_user.uuid}>
                      {t('Job Title')}
                    </label>
                    <Tooltip
                      title={
                        isControlsDisabled
                          ? t(
                              'Editing not available: this user is being managed externally'
                            )
                          : ''
                      }
                    >
                    <Autocomplete
                      sx={{ mt: 1 }}
                      multiple={false}
                      value={jobTitle}
                      options={jobTitleOptions}
                      renderInput={params => (
                        <InputBase
                          inputProps={{
                            ...params.slotProps.htmlInput,
                          }}
                          {...params.slotProps.input}
                          fullWidth
                          onBlur={e =>
                            updateUserObject({
                              job_title: e.currentTarget.value,
                            })
                          }
                          disabled={isControlsDisabled}
                        />
                      )}
                      freeSolo
                      onChange={(e, value) => {
                        value && updateUserObject({ job_title: value.label });
                      }}
                      disabled={isControlsDisabled}
                    />
                    </Tooltip>
                  </MaterialFormGridControl>
                </>
                <>
                  <MaterialFormGridControl
                    size={12}
                  >
                    <label htmlFor={currentContext.current_user.uuid}>
                      {t('Department')}
                    </label>
                    <Tooltip
                      title={
                        isControlsDisabled
                          ? t(
                            'Editing not available: this user is being managed externally'
                          )
                          : ''
                      }
                    >
                    <Autocomplete
                      sx={{ mt: 1 }}
                      multiple={false}
                      value={department}
                      options={departmentOptions}
                      renderInput={params => (
                        <InputBase
                          inputProps={{
                            ...params.slotProps.htmlInput,
                          }}
                          {...params.slotProps.input}
                          fullWidth
                          onBlur={e =>
                            updateUserObject({
                              department: e.currentTarget.value,
                            })
                          }
                          disabled={isControlsDisabled}
                        />
                      )}
                      freeSolo
                      onChange={(e, value) => {
                        value && updateUserObject({ department: value.label });
                      }}
                      disabled={isControlsDisabled}
                    />
                    </Tooltip>
                  </MaterialFormGridControl>
                </>
                {currentContext.current_user.can_manage_users && (
                  <React.Fragment>
                    {isExternal ? null : (
                      <MaterialFormGridControl
                        size={12}
                        GridProps={{ sx: { textAlign: 'center' } }}
                      >
                        <Tooltip
                          title={
                            isControlsDisabled
                              ? t(
                                  'Editing not available: this user is being managed externally'
                                )
                              : ''
                          }
                        >
                          <span>
                            <Button
                              disabled={isControlsDisabled}
                              startIcon={<IShield />}
                              variant="contained"
                              color={superPowersOn ? 'primary' : 'secondary'}
                              onClick={e =>
                                updateUserObject({
                                  user_type: superPowersOn
                                    ? 'operator'
                                    : 'standard',
                                })
                              }
                              size="large"
                            >
                              {superPowersOn
                                ? t('Turn off Super Powers')
                                : t('Turn on Super Powers')}
                            </Button>
                          </span>
                        </Tooltip>
                      </MaterialFormGridControl>
                    )}
                    {isExternal ? null : (
                      <MaterialFormGridCollapse in={superPowersOn}>
                        <MaterialFormGridControl size={12}>
                          <MaterialFormGrid
                            spacing={0}
                            {...bindingProps}
                            onChange={(prop, val) =>
                              updateUserObject({ [prop]: val })
                            }
                          >
                            <MaterialFormGridControl
                              disabled={isControlsDisabled}
                              disabledTooltip={t(
                                'Editing not available: this user is being managed externally'
                              )}
                              size={12}
                              label={t('Manage Organisation')}
                              prop="can_manage_organisation"
                            />
                            <MaterialFormGridControl
                              disabled={isControlsDisabled}
                              disabledTooltip={t(
                                'Editing not available: this user is being managed externally'
                              )}
                              size={12}
                              label={t('Create Projects')}
                              prop="can_create_projects"
                            />
                            <MaterialFormGridControl
                              disabled={isControlsDisabled}
                              disabledTooltip={t(
                                'Editing not available: this user is being managed externally'
                              )}
                              size={12}
                              label={t('Access All Projects')}
                              prop="can_manage_all_projects"
                            />
                            <MaterialFormGridControl
                              disabled={isControlsDisabled}
                              disabledTooltip={t(
                                'Editing not available: this user is being managed externally'
                              )}
                              size={12}
                              label={t('Export')}
                              prop="can_export"
                            />
                            <MaterialFormGridControl
                              disabled={isControlsDisabled}
                              disabledTooltip={t(
                                'Editing not available: this user is being managed externally'
                              )}
                              size={12}
                              label={t('Manage Users')}
                              prop="can_manage_users"
                            />
                            <MaterialFormGridControl
                              disabled={isControlsDisabled}
                              disabledTooltip={t(
                                'Editing not available: this user is being managed externally'
                              )}
                              size={12}
                              label={t(
                                'Manage Templates and Organisation Lists'
                              )}
                              prop="can_manage_templates"
                            />
                            <MaterialFormGridControl
                              disabled={isControlsDisabled}
                              disabledTooltip={t(
                                'Editing not available: this user is being managed externally'
                              )}
                              size={12}
                              label={t('Manage Companies')}
                              prop="can_manage_companies"
                            />
                            <MaterialFormGridControl
                              disabled={isControlsDisabled}
                              disabledTooltip={t(
                                'Editing not available: this user is being managed externally'
                              )}
                              size={12}
                              label={t('Manage Insights')}
                              prop="can_manage_insights"
                            />
                            <MaterialFormGridControl
                              disabled={isControlsDisabled}
                              disabledTooltip={t(
                                'Editing not available: this user is being managed externally'
                              )}
                              size={12}
                              label={t('View Diagnostics')}
                              prop="can_view_diagnostics"
                            />
                            {(isSupportUser ||
                              userState.userObject
                                ?.can_edit_managed_templates) && (
                              <MaterialFormGridControl
                                disabled={isControlsDisabled || !isSupportUser}
                                size={12}
                                label={t('Edit Managed Templates')}
                                prop="can_edit_managed_templates"
                              />
                            )}
                          </MaterialFormGrid>
                        </MaterialFormGridControl>
                      </MaterialFormGridCollapse>
                    )}
                  </React.Fragment>
                )}
              </MaterialFormGrid>
            )}
            {selectedTab === 1 && (
              <ChangeLog
                changeLog={userState.userChangeLog}
                generateLinks={showDiagnostics}
              />
            )}
            {selectedTab === 2 && (
              <Diagnostics
                record={userState.userObject}
                policy={userState.userPolicy}
              />
            )}
          </React.Fragment>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button
          variant="contained"
          color='secondary'
          onClick={e => {
            props.onClose();
            setShouldValidate(false);
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          sx={{ minWidth: '100px' }}
          startIcon={
            saving && (
              <ILoader fontSize="small" />
            )
          }
          disabled={!dirty}
          variant="contained"
          color="primary"
          onClick={e => saveChange()}
        >
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
